import {PrimeIcons} from "primereact/api";
import {Button} from "primereact/button";
import {Dialog} from "primereact/dialog";
import {Message} from "primereact/message";
import {type RefObject, useEffect, useState} from "react";
import {useTranslation} from "react-i18next";

import type {GeoRefAddress} from "@reva/shared/geo-reference";

import {clearAddress, selectAddressAmbiguousData} from "../../redux/addressSlice";
import {useRevaDispatch, useRevaSelector} from "../../redux/revaHooks";
import {AmbiguousAddresses} from "./AmbiguousAddresses";
import {AmbiguousAddressMap} from "./AmbiguousAddressMap";

interface AmbiguousAddressDialogProps {
    addressQuery: string;
    visible: boolean;
    addressInputRef: RefObject<HTMLTextAreaElement | null>;
}

/**
 * Dialog to handle ambiguous address query.
 */
export default function AmbiguousAddressDialog({addressQuery, visible, addressInputRef}: AmbiguousAddressDialogProps) {
    const {t} = useTranslation();
    const dispatch = useRevaDispatch();

    const ambiguousAddresses = useRevaSelector(selectAddressAmbiguousData);

    const [highlightedAddressCandidate, setHighlightedAddressCandidate] = useState<GeoRefAddress | null>(null);

    const highlightedAmbiguousAddress =
        ambiguousAddresses && ambiguousAddresses.addresses.length > 0
            ? highlightedAddressCandidate || ambiguousAddresses.addresses[0]
            : null;

    useEffect(() => {
        setHighlightedAddressCandidate(null);
    }, [addressQuery]);

    const onCancel = () => {
        dispatch(clearAddress());
        if (addressInputRef.current) {
            addressInputRef.current.focus?.();
        }
    };

    const footer = <Button icon={PrimeIcons.PENCIL} severity="danger" label={t("address.adjust")} onClick={onCancel} />;

    return (
        <Dialog
            header={t("address.ambiguous", {addressQuery})}
            visible={visible}
            modal={true}
            onHide={() => {}}
            closable={false}
            style={{width: "95vw", height: "95vh"}}
            footer={footer}
        >
            <div className="w-full h-full flex flex-col">
                <Message severity="warn" text={t("address.multipleFound")} className="mb-4" />
                <div className="grow shrink-0 MapSplit">
                    <div className="MapSplitLeft relative">
                        <div className="w-full h-full pr-4 absolute overflow-x-auto">
                            <AmbiguousAddresses
                                ambiguousAddresses={ambiguousAddresses}
                                highlightedAddress={highlightedAmbiguousAddress}
                                highlightAmbiguousAddress={setHighlightedAddressCandidate}
                            />
                        </div>
                    </div>
                    <div className="MapSplitRight OpenLayersMapBackdrop">
                        <AmbiguousAddressMap highlightAmbiguousAddress={highlightedAmbiguousAddress} />
                    </div>
                </div>
            </div>
        </Dialog>
    );
}
